<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getTime, timeInMinutes } from '../helpers/businessHour';
import { formatMinuteOfDay } from 'shared/helpers/businessHours';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  ariaLabel: { type: String, required: true },
});
const model = defineModel({ type: String, default: '' });
const { t } = useI18n();
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;
const isEditing = ref(false);
const inputTime = ref('');
const pickerContent = ref(null);
const pickerLabel = computed(() =>
  t('BUSINESS_HOURS_SETTINGS.TIME_PICKER', { label: props.ariaLabel })
);
const units = computed(() => [
  {
    key: 'hour',
    label: t('BUSINESS_HOURS_SETTINGS.HOUR'),
    values: Array.from({ length: HOURS_IN_DAY }, (_, value) => value),
  },
  {
    key: 'minute',
    label: t('BUSINESS_HOURS_SETTINGS.MINUTE'),
    values: Array.from({ length: MINUTES_IN_HOUR }, (_, value) => value),
  },
]);
const selectedUnits = computed(() => {
  const minute = model.value ? timeInMinutes(model.value) : 0;
  return {
    hour: Math.floor(minute / MINUTES_IN_HOUR),
    minute: minute % MINUTES_IN_HOUR,
  };
});
watch(
  model,
  value => {
    if (!isEditing.value) {
      inputTime.value = value ? formatMinuteOfDay(timeInMinutes(value)) : '';
    }
  },
  { immediate: true }
);
const updateTime = value => {
  inputTime.value = value;
  if (!TIME_PATTERN.test(value)) {
    model.value = '';
    return;
  }
  const [hour, minute] = value.split(':').map(Number);
  model.value = getTime(hour, minute);
};
const selectUnit = (unit, value, hide) => {
  const selected = { ...selectedUnits.value, [unit]: value };
  updateTime(
    formatMinuteOfDay(selected.hour * MINUTES_IN_HOUR + selected.minute)
  );
  if (unit === 'minute') hide();
};
const scrollToSelection = async () => {
  await nextTick();
  pickerContent.value
    .querySelectorAll('[aria-pressed="true"]')
    .forEach(option => option.scrollIntoView({ block: 'nearest' }));
};
</script>

<template>
  <div
    class="flex items-center w-[7.5rem] min-w-0 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-black2 focus-within:outline-n-brand"
    :class="{ 'opacity-60': disabled }"
  >
    <input
      :value="inputTime"
      type="text"
      :placeholder="t('BUSINESS_HOURS_SETTINGS.TIME_FORMAT')"
      maxlength="5"
      pattern="([01][0-9]|2[0-3]):[0-5][0-9]"
      required
      :disabled="disabled"
      :aria-label="ariaLabel"
      class="reset-base flex-1 min-w-0 !m-0 ps-2 pe-1 py-1.5 rounded-s-lg border-0 outline-none bg-transparent text-body-main text-n-slate-12 placeholder:text-n-slate-10 selection:bg-n-brand selection:text-n-brand-contrast disabled:cursor-not-allowed"
      @focus="isEditing = true"
      @blur="isEditing = false"
      @input="updateTime($event.target.value)"
    />
    <Popover align="start" @show="scrollToSelection">
      <template #default="{ isOpen }">
        <button
          type="button"
          :disabled="disabled"
          :aria-label="pickerLabel"
          aria-haspopup="dialog"
          :aria-expanded="isOpen"
          class="flex shrink-0 items-center justify-center size-7 p-0 me-1 rounded-md text-n-slate-11 hover:bg-n-blue-3 hover:text-n-blue-11 focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand disabled:pointer-events-none disabled:cursor-not-allowed"
        >
          <Icon icon="i-lucide-clock" class="size-3.5 shrink-0" />
        </button>
      </template>
      <template #content="{ hide }">
        <div
          ref="pickerContent"
          role="dialog"
          :aria-label="pickerLabel"
          class="flex gap-2 w-[11rem] p-2 bg-n-solid-1"
        >
          <div v-for="unit in units" :key="unit.key" class="flex-1 min-w-0">
            <p class="m-0 mb-1 text-center text-label-small text-n-slate-11">
              {{ unit.label }}
            </p>
            <div class="flex flex-col gap-1 max-h-[13rem] overflow-y-auto">
              <button
                v-for="value in unit.values"
                :key="value"
                type="button"
                :aria-label="`${unit.label} ${String(value).padStart(2, '0')}`"
                :aria-pressed="value === selectedUnits[unit.key]"
                class="shrink-0 rounded-md px-2 py-1.5 text-body-main tabular-nums focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand"
                :class="
                  value === selectedUnits[unit.key]
                    ? 'bg-n-brand text-n-brand-contrast'
                    : 'text-n-slate-12 hover:bg-n-blue-3 hover:text-n-blue-11'
                "
                @click="selectUnit(unit.key, value, hide)"
              >
                {{ String(value).padStart(2, '0') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </Popover>
  </div>
</template>
